import { useMemo, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Check,
  Clock3,
  Fingerprint,
  GitBranch,
  LockKeyhole,
  Play,
  Plus,
  RotateCcw,
  ShieldCheck,
  X,
} from "lucide-react";
import type {
  CertificateSet,
  MigrationOrdering,
  MigrationPlan,
  MigrationPlanStatus,
  MigrationTargetDefinition,
} from "@edgepki/protocol";
import {
  approveMigrationRecovery,
  approveMigrationPlan,
  createMigrationPlan,
  requestMigrationRecovery,
  startMigrationPlan,
  type ConsoleData,
  type CreateMigrationPlanRequest,
} from "./api.js";

interface MigrationViewProps {
  data: ConsoleData;
  onPlanChange: (plan: MigrationPlan) => void;
  onNavigatePolicies: () => void;
}

interface TargetDraft {
  selected: boolean;
  failureDomain: string;
  healthURL: string;
}

function statusTone(status: MigrationPlanStatus): "good" | "warning" | "danger" | "neutral" {
  if (status === "completed" || status === "approved") return "good";
  if (status === "failed" || status === "expired") return "danger";
  if (["running", "rolling_back", "paused"].includes(status)) return "warning";
  return "neutral";
}

function statusLabel(value: string): string {
  return value.replaceAll("_", " ");
}

function localDateTime(value: Date): string {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function endpointName(endpoint: string): string {
  const value = endpoint.replace(/\/$/, "");
  return value.slice(value.lastIndexOf("/") + 1) || value;
}

function transactionDNS(data: ConsoleData, set: CertificateSet): string | undefined {
  const transaction = data.transactions.find(
    (item) => item.transaction.transaction_id === set.standby?.certificate_id,
  );
  return transaction?.transaction.request.sans.find(
    (san) => !san.includes("://") && !san.includes("@"),
  );
}

function defaultHealthURL(data: ConsoleData, set: CertificateSet): string {
  const dns = transactionDNS(data, set);
  if (dns) return `https://${dns}/health`;
  // A real tenant gets no invented host: the field stays empty with a placeholder.
  if (!data.demo) return "";
  const name = endpointName(set.endpoint_id).replace(/[^a-zA-Z0-9-]/g, "-");
  return `https://${name}.prod.internal.example/health`;
}

function mergePlan(plan: MigrationPlan, patch: Partial<MigrationPlan>): MigrationPlan {
  return { ...plan, ...patch, updated_at: new Date().toISOString() };
}

function demoTarget(data: ConsoleData, set: CertificateSet, draft: TargetDraft, index: number, windowEnd: string): MigrationTargetDefinition {
  const integration = data.integrations.find(
    (item) => item.enabled && (item.kind === "nginx" || item.kind === "apache" || item.kind === "java_keystore" || item.kind === "hashicorp_vault"),
  );
  const nodeID = integration?.node_ids[0] ?? data.nodes[0]?.id ?? "node-demo-01";
  const targetID = `target:${set.id}:${index + 1}`;
  return {
    id: targetID,
    certificate_set_id: set.id,
    endpoint_id: set.endpoint_id,
    profile_id: set.profile_id,
    failure_domain: draft.failureDomain,
    node_id: nodeID,
    generation: set.generation,
    source_certificate_id: set.active?.certificate_id ?? `source-${index + 1}`,
    source_certificate_sha256: "s".repeat(43),
    target_certificate_id: set.standby?.certificate_id ?? `target-${index + 1}`,
    deployment_mode: integration?.kind === "apache" ? "apache" : integration?.kind === "java_keystore" ? "java_keystore" : integration?.kind === "hashicorp_vault" ? "hashicorp_vault" : "nginx",
    adapter_reference: integration?.adapter_reference ?? "local/nginx-production",
    integration_revision: Math.max(1, integration?.revision ?? 1),
    local_generation: Math.max(1, set.generation),
    local_slot: `slot-${String(Math.max(1, set.generation)).padStart(6, "0")}`,
    certificate_sha256: "t".repeat(43),
    health_url: draft.healthURL,
    capability_id: data.capabilities.find((item) => item.endpoint_id === set.endpoint_id)?.id ?? `capability-${index + 1}`,
    capability_sha256: data.capabilities.find((item) => item.endpoint_id === set.endpoint_id)?.evidence_sha256 ?? "c".repeat(43),
    capability_expires_at: new Date(Date.parse(windowEnd) + 60 * 60_000).toISOString(),
  };
}

function createDemoPlan(data: ConsoleData, input: CreateMigrationPlanRequest, drafts: Map<string, TargetDraft>): MigrationPlan {
  const now = new Date();
  const selectedSets = data.certificateSets.filter((set) => drafts.get(set.id)?.selected);
  const targets = selectedSets.map((set, index) => demoTarget(data, set, drafts.get(set.id)!, index, input.maintenance_window.not_after));
  const waveSize = Math.max(1, Math.min(input.safeguards.max_parallel, targets.length));
  const waves = Array.from({ length: Math.ceil(targets.length / waveSize) }, (_, index) => ({
    id: `wave-${String(index + 1).padStart(2, "0")}`,
    sequence: index + 1,
    kind: index === 0 ? ("canary" as const) : ("batch" as const),
    target_ids: targets.slice(index * waveSize, (index + 1) * waveSize).map((target) => target.id),
    prerequisite_wave_ids: index === 0 ? [] : [`wave-${String(index).padStart(2, "0")}`],
  }));
  const id = `migration-demo-${Date.now()}`;
  const predecessorPlan = input.predecessor ? data.migrations.find((plan) => plan.definition.id === input.predecessor?.migration_plan_id) : undefined;
  return {
    definition: {
      protocol: "sectigo-edge.migration-plan.v1",
      id,
      tenant_id: data.overview.tenant_id,
      name: input.name,
      created_by: data.user.id,
      created_at: now.toISOString(),
      ordering: input.ordering,
      target_policy_version: data.policy.version,
      target_policy_sha256: "p".repeat(43),
      policy_impact_sha256: "i".repeat(43),
      graph_commitment_sha256: "g".repeat(43),
      evidence_valid_until: new Date(Date.parse(input.maintenance_window.not_after) + 60 * 60_000).toISOString(),
      maintenance_window: input.maintenance_window,
      safeguards: input.safeguards,
      ...(input.predecessor && predecessorPlan ? { predecessor: {
        migration_plan_id: predecessorPlan.definition.id,
        plan_sha256: predecessorPlan.definition.plan_sha256,
        recovery_sha256: input.predecessor.recovery_sha256,
      } } : {}),
      targets_commitment_sha256: "e".repeat(43),
      targets,
      waves,
      plan_sha256: "m".repeat(43),
    },
    status: "awaiting_approval",
    proposed_by: data.user.id,
    updated_at: now.toISOString(),
    waves: waves.map((wave) => ({
      wave_id: wave.id,
      status: "pending",
      targets: wave.target_ids.map((target_id) => ({ target_id, status: "pending" })),
    })),
  };
}

export function MigrationView({ data, onPlanChange, onNavigatePolicies }: MigrationViewProps) {
  const eligible = useMemo(
    () => data.certificateSets.filter((set) => set.active && set.standby?.state === "staged" && set.standby.health === "healthy"),
    [data.certificateSets],
  );
  const locked = useMemo(
    () => new Set(data.migrations.filter((plan) => ["awaiting_approval", "approved", "running", "paused", "rolling_back"].includes(plan.status)).flatMap((plan) => plan.definition.targets.map((target) => target.certificate_set_id))),
    [data.migrations],
  );
  const [selectedPlanID, setSelectedPlanID] = useState(data.migrations[0]?.definition.id ?? "");
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState("Production cryptography migration");
  const [ordering, setOrdering] = useState<MigrationOrdering>("dependents_first");
  const [windowStart, setWindowStart] = useState(() => localDateTime(new Date()));
  const [windowEnd, setWindowEnd] = useState(() => localDateTime(new Date(Date.now() + 90 * 60_000)));
  const [canaryTargets, setCanaryTargets] = useState(1);
  const [maxParallel, setMaxParallel] = useState(5);
  const [maxPerDomain, setMaxPerDomain] = useState(1);
  const [observationSeconds, setObservationSeconds] = useState(300);
  const [failurePercent, setFailurePercent] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [drafts, setDrafts] = useState<Map<string, TargetDraft>>(() => new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [recoveryRemediation, setRecoveryRemediation] = useState("");
  const [predecessorPlan, setPredecessorPlan] = useState<MigrationPlan | undefined>();

  const fallbackFailureDomain = data.demo ? "production-a" : "";
  const selectedPlan = data.migrations.find((plan) => plan.definition.id === selectedPlanID) ?? data.migrations[0];
  const activePlans = data.migrations.filter((plan) => !["completed", "cancelled", "expired", "failed"].includes(plan.status)).length;
  const completedPlans = data.migrations.filter((plan) => plan.status === "completed").length;

  function openPlanner(predecessor?: MigrationPlan) {
    const predecessorSets = new Set(predecessor?.definition.targets.map((target) => target.certificate_set_id) ?? []);
    const next = new Map<string, TargetDraft>();
    eligible.forEach((set, index) => next.set(set.id, {
      selected: !locked.has(set.id) && (predecessor ? predecessorSets.has(set.id) : index === 0),
      failureDomain: data.nodes.find((node) => node.id === data.integrations.find((integration) => integration.node_ids.includes(node.id))?.node_ids[0])?.name.replace(/\s+/g, "-").toLowerCase() ?? fallbackFailureDomain,
      healthURL: defaultHealthURL(data, set),
    }));
    setDrafts(next);
    setPredecessorPlan(predecessor);
    setName(predecessor ? `${predecessor.definition.name} recovery successor` : "Production cryptography migration");
    setAcknowledged(false);
    setError("");
    setNotice("");
    setFormOpen(true);
  }

  function updateDraft(id: string, patch: Partial<TargetDraft>) {
    setDrafts((current) => {
      const next = new Map(current);
      next.set(id, { ...(next.get(id) ?? { selected: false, failureDomain: fallbackFailureDomain, healthURL: "" }), ...patch });
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const targets = eligible.flatMap((set) => {
      const draft = drafts.get(set.id);
      return draft?.selected ? [{ certificate_set_id: set.id, failure_domain: draft.failureDomain.trim(), health_url: draft.healthURL.trim() }] : [];
    });
    if (!targets.length) { setError("Select at least one staged certificate set."); return; }
    if (maxParallel < canaryTargets) { setError("Maximum parallel targets cannot be smaller than the canary."); return; }
    if (!acknowledged) { setError("Acknowledge the exact evidence and automatic rollback controls."); return; }
    const input: CreateMigrationPlanRequest = {
      name: name.trim(),
      ordering,
      maintenance_window: {
        not_before: new Date(windowStart).toISOString(),
        not_after: new Date(windowEnd).toISOString(),
      },
      safeguards: {
        canary_targets: canaryTargets,
        max_parallel: maxParallel,
        max_targets_per_failure_domain: maxPerDomain,
        observation_seconds: observationSeconds,
        max_wave_failure_percent: failurePercent,
        automatic_rollback: true,
      },
      targets,
      ...(predecessorPlan?.recovery?.status === "approved" ? { predecessor: {
        migration_plan_id: predecessorPlan.definition.id,
        recovery_sha256: predecessorPlan.recovery.recovery_sha256,
      } } : {}),
    };
    setBusy(true);
    setError("");
    try {
      const plan = data.demo ? createDemoPlan(data, input, drafts) : await createMigrationPlan(input);
      onPlanChange(plan);
      setSelectedPlanID(plan.definition.id);
      setFormOpen(false);
      setNotice("Immutable migration plan created. A different administrator must approve its exact digest.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Migration plan could not be created");
    } finally {
      setBusy(false);
    }
  }

  async function approve(plan: MigrationPlan) {
    setBusy(true); setError(""); setNotice("");
    try {
      const next = data.demo
        ? mergePlan(plan, { status: "approved", approved_by: "user:security-reviewer@acme.example", approved_at: new Date().toISOString() })
        : await approveMigrationPlan(plan.definition.id, plan.definition.plan_sha256);
      onPlanChange(next);
      setNotice(data.demo ? "Independent reviewer approval simulated and bound to the plan digest." : "Independent approval recorded for the exact plan digest.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Approval failed"); }
    finally { setBusy(false); }
  }

  async function start(plan: MigrationPlan, simulateFailure = false) {
    setBusy(true); setError(""); setNotice("");
    try {
      const now = new Date().toISOString();
      const running = data.demo
        ? mergePlan(plan, {
            status: "running",
            started_by: data.user.id,
            started_at: now,
            current_wave_id: plan.definition.waves[0]!.id,
            waves: plan.waves.map((wave, index) => index === 0 ? { ...wave, status: "running", started_at: now, targets: wave.targets.map((target) => ({ ...target, status: "commanded", command_id: `promote:${plan.definition.id}:${target.target_id}` })) } : wave),
          })
        : await startMigrationPlan(plan.definition.id, plan.definition.plan_sha256);
      onPlanChange(running);
      setNotice(data.demo ? "Promotion commands dispatched to the simulated Mesh Node." : "First wave dispatched. Nodes will report promotion and sustained health evidence.");
      if (data.demo) {
        window.setTimeout(() => {
          const observedAt = new Date().toISOString();
          const observing = mergePlan(running, { waves: running.waves.map((wave, index) => index === 0 ? { ...wave, status: "observing", observation_due_at: new Date(Date.now() + 1200).toISOString(), targets: wave.targets.map((target) => ({ ...target, status: "observing", promoted_at: observedAt, observation_due_at: new Date(Date.now() + 1200).toISOString(), observation_command_id: `observe:${plan.definition.id}:${target.target_id}` })) } : wave) });
          onPlanChange(observing);
        }, 800);
        if (simulateFailure) {
          window.setTimeout(() => {
            const rollbackAt = new Date().toISOString();
            const rollingBack = mergePlan(running, { status: "rolling_back", pause_reason: "Canary health failed; restoring every promoted peer", waves: running.waves.map((wave, index) => index === 0 ? { ...wave, status: "rolling_back", targets: wave.targets.map((target) => ({ ...target, status: "rollback_commanded", promoted_at: rollbackAt, observation_command_id: `observe:${plan.definition.id}:${target.target_id}`, rollback_command_id: `rollback:${plan.definition.id}:${target.target_id}` })) } : wave) });
            onPlanChange(rollingBack);
          }, 1650);
          window.setTimeout(() => {
            const rolledBackAt = new Date().toISOString();
            const restored = mergePlan(running, { status: "paused", pause_reason: "The complete demo wave was restored to its exact source certificates", waves: running.waves.map((wave, index) => index === 0 ? { ...wave, status: "rolled_back", completed_at: rolledBackAt, targets: wave.targets.map((target) => ({ ...target, status: "rolled_back", failure_code: "simulated_canary_health_failed", rolled_back_at: rolledBackAt })) } : wave) });
            onPlanChange(restored);
          }, 2600);
        } else {
          window.setTimeout(() => {
            const completedAt = new Date().toISOString();
            const completed = mergePlan(running, { status: "completed", completed_at: completedAt, waves: running.waves.map((wave) => ({ ...wave, status: "completed", completed_at: completedAt, targets: wave.targets.map((target) => ({ ...target, status: "healthy", health_verified_at: completedAt })) })) });
            onPlanChange(completed);
          }, 2300);
        }
      }
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Migration could not be started"); }
    finally { setBusy(false); }
  }

  async function requestRecovery(plan: MigrationPlan) {
    if (recoveryRemediation.trim().length < 8) { setError("Describe the verified remediation in at least 8 characters."); return; }
    setBusy(true); setError(""); setNotice("");
    try {
      const now = new Date();
      const next = data.demo
        ? mergePlan(plan, { recovery: {
            protocol: "sectigo-edge.migration-recovery.v1", id: `recovery-demo-${Date.now()}`,
            tenant_id: plan.definition.tenant_id, migration_plan_id: plan.definition.id, plan_sha256: plan.definition.plan_sha256,
            restored_targets_sha256: "r".repeat(43), remediation: recoveryRemediation.trim(), requested_by: data.user.id,
            requested_at: now.toISOString(), expires_at: new Date(now.getTime() + 30 * 60_000).toISOString(),
            recovery_sha256: "v".repeat(43), status: "awaiting_approval",
          } })
        : await requestMigrationRecovery(plan.definition.id, plan.definition.plan_sha256, recoveryRemediation.trim());
      onPlanChange(next);
      setNotice(data.demo ? "Recovery request simulated with exact restored-state evidence." : "Recovery evidence frozen. A different administrator must approve it before target locks are released.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Recovery request failed"); }
    finally { setBusy(false); }
  }

  async function approveRecovery(plan: MigrationPlan) {
    if (!plan.recovery) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const now = new Date().toISOString();
      const next = data.demo
        ? mergePlan(plan, { status: "cancelled", cancelled_at: now, recovery: { ...plan.recovery, status: "approved", approved_by: "user:incident-reviewer@acme.example", approved_at: now } })
        : await approveMigrationRecovery(plan.definition.id, plan.recovery.recovery_sha256);
      onPlanChange(next);
      setNotice("Independent recovery approval recorded. Restored targets are unlocked and a successor can preserve this lineage.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Recovery approval failed"); }
    finally { setBusy(false); }
  }

  return <div className="migration-view">
    <section className="migration-hero">
      <div>
        <span>POLICY-BOUND ZERO-DOWNTIME CHANGE</span>
        <h2>Move cryptography without guessing what breaks</h2>
        <p>Every wave is deterministically built from fresh capability evidence and the live trust graph. Exact source and target certificates remain rollback-bound until sustained health proves the change.</p>
      </div>
      <div className="migration-summary">
        <article><strong>{data.migrations.length}</strong><span>immutable plans</span></article>
        <article><strong>{activePlans}</strong><span>active or awaiting approval</span></article>
        <article><strong>{completedPlans}</strong><span>completed with evidence</span></article>
        <article><strong>{eligible.length}</strong><span>healthy staged targets</span></article>
      </div>
    </section>

    <section className="migration-toolbar">
      <div><strong>Migration control</strong><p>Policies define who may plan and approve; safeguards define how execution may proceed.</p></div>
      <div><button className="button" onClick={onNavigatePolicies}><ShieldCheck size={16} /> Configure policies</button><button className="button button--primary" onClick={() => openPlanner()}><Plus size={16} /> Create migration</button></div>
    </section>

    {(error || notice) && <div className={`migration-message migration-message--${error ? "error" : "notice"}`}>{error ? <AlertTriangle size={17} /> : <BadgeCheck size={17} />}<span>{error || notice}</span></div>}

    {formOpen && <form className="migration-planner" onSubmit={submit}>
      <header><div><span>{predecessorPlan ? "VERIFIED RECOVERY SUCCESSOR" : "NEW IMMUTABLE PLAN"}</span><h3>Plan dependency-aware certificate promotion</h3><p>{predecessorPlan ? `This successor commits recovery ${predecessorPlan.recovery?.recovery_sha256 ?? ""}; policy, topology, capabilities, targets, and maintenance evidence are rebuilt fresh.` : "The server rebuilds every target from live inventory; browser-supplied certificate IDs, policy proofs, and graph digests are never trusted."}</p></div><button type="button" aria-label="Close planner" onClick={() => setFormOpen(false)}><X size={19} /></button></header>
      <div className="migration-form-grid">
        <label className="migration-field migration-field--wide"><span>Plan name</span><input required minLength={3} maxLength={120} value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label className="migration-field"><span>Dependency ordering</span><select value={ordering} onChange={(event) => setOrdering(event.target.value as MigrationOrdering)}><option value="dependents_first">Callers first</option><option value="providers_first">Providers first</option></select><small>Callers first is safest when upgrading trust compatibility.</small></label>
        <label className="migration-field"><span>Observation period</span><select value={observationSeconds} onChange={(event) => setObservationSeconds(Number(event.target.value))}><option value={30}>30 seconds</option><option value={300}>5 minutes</option><option value={900}>15 minutes</option><option value={3600}>1 hour</option></select><small>Health must remain good for the full period.</small></label>
        <label className="migration-field"><span>Window starts</span><input type="datetime-local" required value={windowStart} onChange={(event) => setWindowStart(event.target.value)} /></label>
        <label className="migration-field"><span>Window ends</span><input type="datetime-local" required value={windowEnd} onChange={(event) => setWindowEnd(event.target.value)} /></label>
        <label className="migration-field"><span>Canary targets</span><input type="number" min={1} max={50} value={canaryTargets} onChange={(event) => setCanaryTargets(Number(event.target.value))} /></label>
        <label className="migration-field"><span>Maximum parallel</span><input type="number" min={1} max={500} value={maxParallel} onChange={(event) => setMaxParallel(Number(event.target.value))} /></label>
        <label className="migration-field"><span>Per failure domain</span><input type="number" min={1} max={50} value={maxPerDomain} onChange={(event) => setMaxPerDomain(Number(event.target.value))} /></label>
        <label className="migration-field"><span>Wave failure budget</span><div className="migration-unit-input"><input type="number" min={0} max={50} step={1} value={failurePercent} onChange={(event) => setFailurePercent(Number(event.target.value))} /><b>%</b></div><small>Recorded in halt evidence; every target rollback pauses expansion.</small></label>
      </div>
      <div className="migration-targets"><header><div><strong>Exact staged targets</strong><span>{[...drafts.values()].filter((draft) => draft.selected).length} selected</span></div><p>Failure domains prevent a wave from concentrating risk in one site, cluster, or availability zone.</p></header>
        {eligible.length === 0 && <div className="migration-empty"><AlertTriangle size={20} /><div><strong>No eligible staged certificate sets</strong><p>Stage a healthy standby on an NGINX, Apache, Java PKCS#12, or HashiCorp Vault integration with current node evidence first.</p></div></div>}
        {eligible.map((set) => { const draft = drafts.get(set.id) ?? { selected: false, failureDomain: fallbackFailureDomain, healthURL: defaultHealthURL(data, set) }; const isLocked = locked.has(set.id); return <article className={draft.selected ? "selected" : ""} key={set.id}>
          <label className="migration-target-check"><input type="checkbox" checked={draft.selected} disabled={isLocked} onChange={(event) => updateDraft(set.id, { selected: event.target.checked })} /><span><strong>{endpointName(set.endpoint_id)}</strong><code>{set.endpoint_id}</code></span></label>
          <label><span>Failure domain</span><input required={draft.selected} disabled={!draft.selected} placeholder="e.g. production-a" value={draft.failureDomain} onChange={(event) => updateDraft(set.id, { failureDomain: event.target.value })} /></label>
          <label><span>HTTPS health proof</span><input type="url" pattern="https://.*" required={draft.selected} disabled={!draft.selected} placeholder="https://service.your-domain.com/health" value={draft.healthURL} onChange={(event) => updateDraft(set.id, { healthURL: event.target.value })} /></label>
          <div className="migration-target-meta"><span>source <code>{set.active?.certificate_id}</code></span><ArrowRight size={14} /><span>target <code>{set.standby?.certificate_id}</code></span>{isLocked && <b>locked by active plan</b>}</div>
        </article>; })}
      </div>
      <label className="migration-ack"><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} /><span><strong>Require independent approval and automatic exact rollback</strong><small>I understand this plan is bound to the current policy, dependency graph, capability evidence, source certificates, target certificates, and maintenance window. Any drift fails closed.</small></span></label>
      <footer><button type="button" className="button" onClick={() => setFormOpen(false)}>Cancel</button><button className="button button--primary" disabled={busy || !acknowledged || eligible.length === 0}><LockKeyhole size={16} /> {busy ? "Committing evidence…" : "Create immutable plan"}</button></footer>
    </form>}

    {!formOpen && data.migrations.length === 0 && <section className="migration-empty migration-empty--large"><GitBranch size={27} /><div><strong>No migration plans yet</strong><p>Create a plan to turn staged dual certificates into dependency-ordered canary waves with two-person approval and automatic rollback.</p><button className="button button--primary" onClick={() => openPlanner()}><Plus size={16} /> Create first migration</button></div></section>}

    {!formOpen && data.migrations.length > 0 && <div className="migration-layout">
      <aside className="migration-plan-list"><span>PLANS</span>{data.migrations.map((plan) => <button className={selectedPlan?.definition.id === plan.definition.id ? "active" : ""} key={plan.definition.id} onClick={() => setSelectedPlanID(plan.definition.id)}><div><strong>{plan.definition.name}</strong><small>{plan.definition.targets.length} target{plan.definition.targets.length === 1 ? "" : "s"} · {plan.definition.waves.length} wave{plan.definition.waves.length === 1 ? "" : "s"}</small></div><i className={`migration-status migration-status--${statusTone(plan.status)}`}>{statusLabel(plan.status)}</i></button>)}</aside>
      {selectedPlan && <main className="migration-plan-detail">
        <header className="migration-plan-head"><div><span>IMMUTABLE EXECUTION</span><h3>{selectedPlan.definition.name}</h3><p>{selectedPlan.definition.ordering === "dependents_first" ? "Callers migrate before the services they depend on." : "Providers migrate before their callers."}</p></div><i className={`migration-status migration-status--${statusTone(selectedPlan.status)}`}>{statusLabel(selectedPlan.status)}</i></header>
        <div className="migration-guardrails"><article><Fingerprint size={17} /><div><span>Plan digest</span><code>{selectedPlan.definition.plan_sha256}</code></div></article><article><Clock3 size={17} /><div><span>Maintenance window</span><strong>{new Date(selectedPlan.definition.maintenance_window.not_before).toLocaleString()} – {new Date(selectedPlan.definition.maintenance_window.not_after).toLocaleString()}</strong></div></article><article><RotateCcw size={17} /><div><span>Rollback boundary</span><strong>Automatic · exact source certificate</strong></div></article></div>
        <section className="migration-waves"><header><strong>Deterministic execution waves</strong><span>{selectedPlan.definition.safeguards.observation_seconds}s sustained observation</span></header><div className="migration-wave-track">{selectedPlan.definition.waves.map((wave, index) => { const execution = selectedPlan.waves.find((item) => item.wave_id === wave.id); return <div className="migration-wave-segment" key={wave.id}><article><span>{wave.kind === "canary" ? "CANARY" : `WAVE ${wave.sequence}`}</span><strong>{wave.target_ids.length} target{wave.target_ids.length === 1 ? "" : "s"}</strong><i className={`migration-status migration-status--${statusTone(execution?.status === "completed" ? "completed" : execution?.status === "failed" || execution?.status === "rolled_back" ? "failed" : execution?.status === "running" || execution?.status === "observing" || execution?.status === "rolling_back" ? "running" : "awaiting_approval")}`}>{statusLabel(execution?.status ?? "pending")}</i><div>{execution?.targets.map((target) => <span title={target.target_id} className={`migration-target-state migration-target-state--${target.status}`} key={target.target_id}>{target.status === "healthy" ? <Check size={12} /> : target.status === "rolled_back" ? <RotateCcw size={12} /> : <Clock3 size={12} />}{statusLabel(target.status)}</span>)}</div></article>{index < selectedPlan.definition.waves.length - 1 && <ArrowRight size={20} />}</div>; })}</div></section>
        {selectedPlan.pause_reason && <div className="migration-message migration-message--error"><AlertTriangle size={17} /><span><strong>Execution halted</strong>{selectedPlan.pause_reason}</span></div>}
        {selectedPlan.status === "paused" && !selectedPlan.recovery && <section className="migration-recovery"><header><div><strong>Close the rollback incident safely</strong><p>First re-stage every exact target from the Mesh Node. Then freeze a remediation statement and restored-state digest for independent approval.</p></div><RotateCcw size={20} /></header><label><span>Verified remediation</span><textarea minLength={8} maxLength={500} value={recoveryRemediation} onChange={(event) => setRecoveryRemediation(event.target.value)} placeholder="Health fault isolated; target re-staged and verified from the assigned Mesh Node." /></label><button className="button button--primary" disabled={busy || recoveryRemediation.trim().length < 8} onClick={() => void requestRecovery(selectedPlan)}><LockKeyhole size={16} /> Freeze recovery evidence</button></section>}
        {selectedPlan.status === "paused" && selectedPlan.recovery?.status === "awaiting_approval" && <section className="migration-recovery migration-recovery--approval"><header><div><strong>Independent recovery approval required</strong><p>The restored estate is committed to <code>{selectedPlan.recovery.restored_targets_sha256}</code>.</p></div><BadgeCheck size={20} /></header><dl><dt>Requested by</dt><dd><code>{selectedPlan.recovery.requested_by}</code></dd><dt>Expires</dt><dd>{new Date(selectedPlan.recovery.expires_at).toLocaleString()}</dd><dt>Remediation</dt><dd>{selectedPlan.recovery.remediation}</dd></dl><button className="button button--primary" disabled={busy || (!data.demo && selectedPlan.recovery.requested_by === data.user.id)} title={!data.demo && selectedPlan.recovery.requested_by === data.user.id ? "A different immutable administrator must approve" : undefined} onClick={() => void approveRecovery(selectedPlan)}><BadgeCheck size={16} /> {data.demo ? "Simulate independent recovery approval" : "Approve exact recovery digest"}</button></section>}
        {selectedPlan.status === "cancelled" && selectedPlan.recovery?.status === "approved" && <section className="migration-recovery migration-recovery--closed"><header><div><strong>Rollback incident closed with evidence</strong><p>Recovery <code>{selectedPlan.recovery.recovery_sha256}</code> was approved by <code>{selectedPlan.recovery.approved_by}</code>.</p></div><Check size={20} /></header><button className="button button--primary" disabled={busy} onClick={() => openPlanner(selectedPlan)}><GitBranch size={16} /> Create verified successor</button></section>}
        <div className="migration-action-row"><div><span>Proposed by</span><code>{selectedPlan.proposed_by}</code>{selectedPlan.approved_by && <><span>Approved by</span><code>{selectedPlan.approved_by}</code></>}</div>{selectedPlan.status === "awaiting_approval" && <button className="button button--primary" disabled={busy || (!data.demo && selectedPlan.proposed_by === data.user.id)} title={!data.demo && selectedPlan.proposed_by === data.user.id ? "A different immutable user must approve" : undefined} onClick={() => void approve(selectedPlan)}><BadgeCheck size={16} /> {data.demo ? "Simulate independent approval" : "Approve exact digest"}</button>}{selectedPlan.status === "approved" && (data.demo ? <div className="migration-demo-actions"><button className="button" disabled={busy} onClick={() => void start(selectedPlan, true)}><RotateCcw size={16} /> Simulate failed canary</button><button className="button button--primary" disabled={busy} onClick={() => void start(selectedPlan)}><Play size={16} /> Run healthy wave</button></div> : <button className="button button--primary" disabled={busy || Date.now() < Date.parse(selectedPlan.definition.maintenance_window.not_before) || Date.now() >= Date.parse(selectedPlan.definition.maintenance_window.not_after)} onClick={() => void start(selectedPlan)}><Play size={16} /> Start first wave</button>)}</div>
      </main>}
      <aside className="migration-explainer"><span>WHAT HAPPENS</span>{[
        ["1", "Evidence is frozen", "The policy, complete dependency graph, endpoint capabilities, and exact certificate digests become one immutable plan."],
        ["2", "A second person approves", "The approver must be a different immutable SSO/SCIM identity with fresh MFA and policy authority."],
        ["3", "Canary commands leave outbound", "Each Mesh Node validates its adapter, local generation, current source, staged target, policy, and expiry."],
        ["4", "Health is sustained", "A fresh TLS connection must serve the exact target certificate throughout the observation boundary."],
        ["5", "Expand or restore", "Healthy evidence unlocks the next wave. One failure restores that target and every promoted peer in its wave to their exact sources before the plan pauses."],
      ].map(([step, heading, body]) => <article key={step}><b>{step}</b><div><strong>{heading}</strong><p>{body}</p></div></article>)}</aside>
    </div>}
  </div>;
}
